import * as React from 'react';
import { Avatar, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, List, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { ArrowBack, Close, Send } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createComment, getComments } from '../../controllers/posts/comments';
import { getSinglePost } from '../../controllers/posts/posts';
import { markConversationRead, touchConversation } from '../../controllers/conversation/conversation';
import { UserContext } from '../../context/UserContext';
import { MessageItem } from '../messages/SingleConversation';
import { InPersonBadge } from '../InPersonBadge';

const NAV = '#202c41';
const BG = '#F2F4F8';

// A Mingle pairing's thread, not a real Message thread: it already knows its
// root status id (no ancestor-walk needed), and it disappears once the event
// ends. "Send a real message" starts a genuinely separate, permanent
// conversation via the normal Messages flow instead of extending this one.
//
// `person` is one avatar's worth of data: { otherAccount, conversations }.
// Someone met at multiple events has one conversation per event — if there's
// more than one, the modal opens on an event picker instead of jumping
// straight into a thread.
export default function ConversationModal({ open, setOpen, person, onStartRealMessage }) {
  const { userDetails } = React.useContext(UserContext);
  const token = userDetails?.token ?? '';
  const [content, setContent] = React.useState('');
  const [activeConversationId, setActiveConversationId] = React.useState(null);

  const conversations = person?.conversations ?? [];
  const hasChoice = conversations.length > 1;

  React.useEffect(() => {
    if (!open) { setActiveConversationId(null); return }
    setActiveConversationId(conversations.length === 1 ? conversations[0].conversation_id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person]);

  const activeConversation = conversations.find(c => c.conversation_id === activeConversationId) ?? null;
  const root_id = activeConversation?.status_id;
  const conversation_id = activeConversation?.conversation_id;

  const { data } = useQuery({
    queryKey: ['getComments', root_id, token],
    queryFn: () => getComments(root_id, token),
    enabled: open && !!root_id,
  });
  const { data: post } = useQuery({
    queryKey: ['getSinglePost', root_id, token],
    queryFn: () => getSinglePost(root_id, token),
    enabled: open && !!root_id,
  });
  const root_post = post ?? {};
  const messages = data?.descendants ?? [];

  const queryClient = useQueryClient();

  // Opening a thread is what "reads" it — same rule as regular Messages, so
  // it disappears from any unread indicator on the Mingle page.
  React.useEffect(() => {
    if (!conversation_id || !token || !userDetails?.id) return;
    markConversationRead({ token, id: conversation_id, user: userDetails.id })
      .then(() => queryClient.invalidateQueries({ queryKey: ['getMyEventConversations'] }));
  }, [conversation_id, token, userDetails?.id]);

  const mutation = useMutation({
    mutationFn: async (vars) => {
      const status = await createComment(vars);
      if (conversation_id) {
        await touchConversation({ token, id: conversation_id, author: userDetails?.id });
      }
      return status;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getComments', root_id, token] });
      queryClient.invalidateQueries({ queryKey: ['getMyEventConversations'] });
      setContent('');
    },
  });

  const send = () => {
    if (!content.trim()) return;
    mutation.mutate({
      body: {
        status: '@' + person.otherAccount?.acct + ' ' + content,
        in_reply_to_id: root_id,
        visibility: 'public',
      },
      token,
    });
  };

  const handleClose = () => setOpen(false);
  const showList = hasChoice && !activeConversation;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {hasChoice && activeConversation && (
          <IconButton onClick={() => setActiveConversationId(null)} size="small">
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Avatar src={person?.otherAccount?.avatar} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={15} noWrap>
            {person?.otherAccount?.display_name || person?.otherAccount?.username}
          </Typography>
          {activeConversation && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Chip
                size="small"
                label={activeConversation.event_title}
                sx={{ background: 'rgba(115,41,130,0.1)', color: '#732982', fontWeight: 700 }}
              />
              <InPersonBadge isInPerson={activeConversation.is_in_person} />
            </Box>
          )}
        </Box>
        <IconButton onClick={handleClose}><Close /></IconButton>
      </DialogTitle>

      {showList ? (
        <DialogContent dividers sx={{ minHeight: 200 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            You've partied with {person?.otherAccount?.username} at more than one event — pick one:
          </Typography>
          <List>
            {conversations.map((c) => (
              <ListItemButton
                key={c.conversation_id}
                onClick={() => setActiveConversationId(c.conversation_id)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText primary={c.event_title} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InPersonBadge isInPerson={c.is_in_person} />
                  {c.unread && (
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#E40303' }} />
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      ) : (
        <>
          <DialogContent dividers sx={{ background: BG, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 320 }}>
            {!data ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
            ) : (
              <>
                <MessageItem item={root_post} last_post={messages.length === 0} />
                {[...messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((item, i, arr) => (
                  <MessageItem item={item} last_post={i === arr.length - 1} key={item.id ?? i} />
                ))}
              </>
            )}
          </DialogContent>
          <Box sx={{ p: 1.5, borderTop: '1px solid #ececf0', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
              placeholder="Message…"
              multiline
              maxRows={4}
              fullWidth
              size="small"
              sx={{ background: BG, '& .MuiOutlinedInput-root': { borderRadius: 4, '& fieldset': { border: 'none' } } }}
            />
            <IconButton
              onClick={send}
              disabled={!content.trim() || mutation.isPending}
              sx={{ background: NAV, color: '#fff', width: 40, height: 40, '&.Mui-disabled': { background: BG } }}
            >
              {mutation.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Send sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
          <Box sx={{ p: 1.5, pt: 0 }}>
            <Typography
              onClick={() => { handleClose(); onStartRealMessage?.(person.otherAccount); }}
              sx={{ fontSize: 12.5, color: '#732982', fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}
            >
              Want to keep talking after the event? Send a real message →
            </Typography>
          </Box>
        </>
      )}
    </Dialog>
  );
}
